import { useState, useEffect } from 'react'
import { Trash2, Download, Copy, Check, Edit3, Link, FileVideo, HardDrive, Calendar, Tag, X, Upload } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function ArchiveDetail({ file, onDelete, onRename, setStatus, layout = 'single', uploadSlot = null }) {
  const { api, apiKey } = useAuth()
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [copied, setCopied] = useState(null)
  const [restoring, setRestoring] = useState(false)

  // Tags (D1-backed)
  const [tbtvId, setTbtvId] = useState(null)
  const [tags, setTags] = useState([])
  const [tagInput, setTagInput] = useState('')
  const [allTags, setAllTags] = useState([])
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [saving, setSaving] = useState(false)

  const meta = file.customMetadata || {}

  useEffect(() => {
    if (file) {
      setTags([])
      setTagInput('')
      setTbtvId(null)
      resolveContent()
      loadAllTags()
    }
  }, [file?.filename])

  async function resolveContent() {
    try {
      let streamUID = meta.streamUID || null
      let originalTitle = meta.originalTitle || null
      try {
        const r2Meta = await api(`/archive/meta/${encodeURIComponent(file.filename)}`)
        streamUID = r2Meta.customMetadata?.streamUID || streamUID
        originalTitle = r2Meta.customMetadata?.originalTitle || originalTitle
      } catch {}

      const res = await api('/content/resolve', {
        method: 'POST',
        body: JSON.stringify({
          archive_key: file.filename,
          stream_uid: streamUID,
          title: originalTitle || file.filename,
          content_type: 'archive',
        }),
      })
      setTbtvId(res.tbtv_id)
      const tagRes = await api(`/tags/${res.tbtv_id}`)
      setTags(tagRes.tags || [])
    } catch {}
  }

  async function loadAllTags() {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }

  function addTag(tag) {
    const clean = tag.trim().toLowerCase()
    if (!clean || tags.includes(clean)) return
    setTags(prev => [...prev, clean])
    setTagInput('')
    setShowTagSuggestions(false)
  }

  function removeTag(tag) {
    setTags(prev => prev.filter(t => t !== tag))
  }

  function handleTagKeyDown(e) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(tagInput)
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      removeTag(tags[tags.length - 1])
    }
  }

  async function handleSaveTags() {
    if (!tbtvId) return
    setSaving(true)
    try {
      await api(`/tags/${tbtvId}`, {
        method: 'PUT',
        body: JSON.stringify({ tags }),
      })
      setStatus('✅ Tags saved')
      loadAllTags()
    } finally {
      setSaving(false)
    }
  }

  const filteredSuggestions = tagInput.length > 0
    ? allTags.filter(t => t.includes(tagInput.toLowerCase()) && !tags.includes(t))
    : []

  function handleStartRename() {
    setNewName(file.filename.replace(/\.mp4$/i, ''))
    setRenaming(true)
  }

  function handleConfirmRename() {
    let clean = newName.trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, '-')
    if (!clean) return
    if (!clean.endsWith('.mp4')) clean += '.mp4'
    if (clean === file.filename) {
      setRenaming(false)
      return
    }
    onRename(file.filename, clean)
    setRenaming(false)
  }

  function handleCancelRename() {
    setRenaming(false)
  }

  async function handleCopy(text, label) {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      setStatus('Failed to copy to clipboard')
    }
  }

  function handleDownload() {
    const API_BASE = import.meta.env.VITE_API_BASE || '/api'
    const url = `${API_BASE}/archive/download/${encodeURIComponent(file.filename)}`
    const a = document.createElement('a')
    a.href = url + `?key=${encodeURIComponent(apiKey)}`
    a.download = file.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setStatus('⬇️ Download started')
  }

  function handleDelete() {
    if (!confirm(`Delete "${file.filename}" from archive?\n\nThis cannot be undone!`)) return
    onDelete(file.filename)
  }

  async function handleRestore() {
    const originalTitle = meta.originalTitle || file.filename.replace(/\.mp4$/i, '')
    const defaultTitle = `RESTORE - ${originalTitle}`
    const title = prompt('Title for restored video in Stream:', defaultTitle)
    if (title === null) return

    setRestoring(true)
    setStatus('⏳ Restoring to Stream...')
    try {
      const res = await api('/restore-to-stream', {
        method: 'POST',
        body: JSON.stringify({ filename: file.filename, title: title || defaultTitle }),
      })

      const tagMsg = res.copiedTags?.length > 0
        ? ` | Tags copied: ${res.copiedTags.join(', ')}`
        : ''
      setStatus(`✅ ${res.message}${tagMsg}`)
    } catch (e) {
      setStatus(`❌ Restore failed: ${e.message}`)
    } finally {
      setRestoring(false)
    }
  }

  const r2PublicUrl = `https://tbtv-archive.tenband.tv/${encodeURIComponent(file.filename)}`
  const API_BASE = import.meta.env.VITE_API_BASE || '/api'
  const streamUrl = `${API_BASE}/archive/download/${encodeURIComponent(file.filename)}?key=${encodeURIComponent(apiKey)}`
  const shareUrl = `${window.location.origin}${API_BASE}/archive/download/${encodeURIComponent(file.filename)}`

  // --- Blocks for layout ---

  const playerBlock = (
    <>
      {/* Header */}
      <div className="archive-detail-header">
        <FileVideo size={20} />
        {renaming ? (
          <div className="rename-controls">
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleConfirmRename()
                if (e.key === 'Escape') handleCancelRename()
              }}
              autoFocus
            />
            <span className="rename-ext">.mp4</span>
            <button onClick={handleConfirmRename} className="small-btn">Save</button>
            <button onClick={handleCancelRename} className="small-btn secondary">Cancel</button>
          </div>
        ) : (
          <div className="archive-detail-title">
            <h3>{file.filename}</h3>
            <button onClick={handleStartRename} className="icon-btn" title="Rename">
              <Edit3 size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Video Player */}
      <div className="archive-player-container">
        <div className="archive-player-wrap">
          <video
            key={file.filename}
            controls
            preload="metadata"
            className="archive-video-player"
          >
            <source src={streamUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </>
  )

  const editBlock = (
    <>
      {/* Metadata */}
      <div className="archive-meta-grid">
        <div className="meta-item">
          <HardDrive size={14} />
          <span className="meta-label">Size</span>
          <span className="meta-value">{formatBytes(file.size)}</span>
        </div>
        <div className="meta-item">
          <Calendar size={14} />
          <span className="meta-label">Archived</span>
          <span className="meta-value">
            {file.uploaded ? new Date(file.uploaded).toLocaleString() : '—'}
          </span>
        </div>
        {meta.streamUID && (
          <div className="meta-item">
            <Tag size={14} />
            <span className="meta-label">Stream UID</span>
            <span className="meta-value mono">{meta.streamUID}</span>
            <button onClick={() => handleCopy(meta.streamUID, 'uid')} className="copy-btn" title="Copy UID">
              {copied === 'uid' ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
        )}
        {meta.originalTitle && (
          <div className="meta-item">
            <FileVideo size={14} />
            <span className="meta-label">Original Title</span>
            <span className="meta-value">{meta.originalTitle}</span>
          </div>
        )}
        {meta.archivedAt && (
          <div className="meta-item">
            <Calendar size={14} />
            <span className="meta-label">Archive Date</span>
            <span className="meta-value">{new Date(meta.archivedAt).toLocaleString()}</span>
          </div>
        )}
        {tbtvId && (
          <div className="meta-item">
            <FileVideo size={14} />
            <span className="meta-label">TBTV ID</span>
            <span className="meta-value mono">{tbtvId}</span>
            <button onClick={() => handleCopy(tbtvId, 'tbtv')} className="copy-btn" title="Copy TBTV ID">
              {copied === 'tbtv' ? <Check size={12} /> : <Copy size={12} />}
            </button>
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="archive-tags-section">
        <label>
          <Tag size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Tags
        </label>
        <div className="tags-input-wrap">
          <div className="tags-chip-list">
            {tags.map(tag => (
              <span key={tag} className="archive-tag-chip">
                {tag}
                <button className="tag-remove" onClick={() => removeTag(tag)}><X size={12} /></button>
              </span>
            ))}
            <input
              type="text"
              className="tag-text-input"
              value={tagInput}
              onChange={e => {
                setTagInput(e.target.value)
                setShowTagSuggestions(true)
              }}
              onKeyDown={handleTagKeyDown}
              onFocus={() => setShowTagSuggestions(true)}
              onBlur={() => setTimeout(() => setShowTagSuggestions(false), 150)}
              placeholder={tags.length === 0 ? 'Add tags...' : ''}
            />
          </div>
          {showTagSuggestions && filteredSuggestions.length > 0 && (
            <div className="tag-suggestions">
              {filteredSuggestions.map(s => (
                <div key={s} className="tag-suggestion" onMouseDown={() => addTag(s)}>
                  {s}
                </div>
              ))}
            </div>
          )}
        </div>
        <button onClick={handleSaveTags} disabled={saving || !tbtvId} style={{ marginTop: 8 }}>
          Save Tags
        </button>
      </div>

      {/* URLs */}
      <div className="archive-urls">
        <h4>
          <Link size={14} style={{ marginRight: 6 }} />
          File Reference
        </h4>
        <div className="url-row">
          <span className="url-label">Download URL:</span>
          <code className="url-value">{shareUrl}</code>
          <button onClick={() => handleCopy(shareUrl, 'url')} className="copy-btn" title="Copy download URL">
            {copied === 'url' ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
        <div className="url-row">
          <span className="url-label">R2 Key:</span>
          <code className="url-value">{file.filename}</code>
          <button onClick={() => handleCopy(file.filename, 'key')} className="copy-btn" title="Copy R2 key">
            {copied === 'key' ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
        <div className="url-row">
          <span className="url-label">Bucket:</span>
          <code className="url-value">tbtv-archive</code>
          <button onClick={() => handleCopy('tbtv-archive', 'bucket')} className="copy-btn" title="Copy bucket name">
            {copied === 'bucket' ? <Check size={12} /> : <Copy size={12} />}
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="archive-actions">
        <button onClick={handleRestore} className="restore-btn" disabled={restoring}>
          <Upload size={14} /> {restoring ? 'Restoring...' : 'Restore to Stream'}
        </button>
        <button onClick={handleDownload} className="download-btn">
          <Download size={14} /> Download MP4
        </button>
        <button onClick={handleDelete} className="danger">
          <Trash2 size={14} /> Delete from Archive
        </button>
      </div>
    </>
  )

  return (
    <div className="archive-detail">
      {layout === 'split' ? (
        <div className="archive-detail-split">
          <div className="archive-detail-split-left">
            {playerBlock}
          </div>
          <div className="archive-detail-split-right">
            {editBlock}
            {uploadSlot}
          </div>
        </div>
      ) : (
        <>
          {playerBlock}
          {editBlock}
        </>
      )}
    </div>
  )
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '—'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}
